import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader, Panel, Empty, Select } from "./ui";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, Download, Activity, LogIn, LogOut, Eye, Zap, Trash2 } from "lucide-react";

const TYPE_META = {
  login: { label: "Login", color: "#16a34a", icon: LogIn },
  logout: { label: "Logout", color: "#6b7280", icon: LogOut },
  screen_view: { label: "Screen view", color: "#0369a1", icon: Eye },
  action: { label: "Action", color: "#00857C", icon: Zap },
};

const TYPE_OPTIONS = [
  { value: "login", label: "Logins" },
  { value: "logout", label: "Logouts" },
  { value: "screen_view", label: "Screen views" },
  { value: "action", label: "Business actions" },
];

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-ZA", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const TypeBadge = ({ type }) => {
  const m = TYPE_META[type] || TYPE_META.action;
  const Icon = m.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wide"
      style={{ background: `${m.color}1a`, color: m.color }}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
};

const Stat = ({ label, value, color = "#0B2545" }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-5 text-center" style={{ borderTop: `3px solid ${color}` }}>
    <div className="overline text-slate-500">{label}</div>
    <div className="font-head text-4xl font-bold mt-2" style={{ color }}>{value}</div>
  </div>
);

export default function ActivityLog() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actor, setActor] = useState("");
  const [type, setType] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (actor) params.actor = actor;
    if (type) params.type = type;
    if (start) params.start = start;
    if (end) params.end = end;
    api.get("/activity", { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [actor, type, start, end]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => { setActor(""); setType(""); setStart(""); setEnd(""); };

  const clearLog = () => {
    if (!window.confirm("Clear the entire activity log? This permanently deletes all recorded logins, screen views and actions. This cannot be undone.")) return;
    api.post("/activity/clear").then((r) => {
      toast.success(`Activity log cleared — ${r.data.deleted} events removed`);
      load();
    }).catch(() => toast.error("Could not clear the activity log"));
  };

  const exportCsv = () => {
    const rows = (data?.events || []).map((e) =>
      [fmt(e.at), e.name || e.actor, e.role, e.type, e.screen || e.action, (e.detail || "").replace(/"/g, "'")]
        .map((v) => `"${v ?? ""}"`).join(","));
    const csv = ["Time,User,Role,Type,Screen/Action,Detail", ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "netstar_activity_log.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const summary = data?.summary;
  const events = data?.events || [];

  return (
    <div>
      <PageHeader title="User Activity Log" subtitle="Monitor usage during testing & rollout — who signed in, which screens they opened, and what they did.">
        <Button data-testid="activity-refresh" onClick={load} variant="outline" className="rounded-sm border-slate-300 font-head">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button data-testid="activity-export-csv" onClick={exportCsv} className="rounded-sm bg-[#0B2545] hover:bg-[#0B2545]/90 font-head">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
        <Button data-testid="activity-clear-log" onClick={clearLog} variant="outline" className="rounded-sm border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 font-head">
          <Trash2 className="h-4 w-4 mr-2" /> Clear log
        </Button>
      </PageHeader>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Stat label="Events shown" value={summary.total} color="#0B2545" />
          <Stat label="Logins" value={summary.type_counts.find((t) => t.type === "login")?.count || 0} color="#16a34a" />
          <Stat label="Screen views" value={summary.type_counts.find((t) => t.type === "screen_view")?.count || 0} color="#0369a1" />
          <Stat label="Business actions" value={summary.type_counts.find((t) => t.type === "action")?.count || 0} color="#00857C" />
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Panel title="Most active users">
            {summary.most_active_users.length === 0 ? <Empty>No activity yet</Empty> : (
              <table className="w-full text-sm" data-testid="activity-top-users">
                <tbody>
                  {summary.most_active_users.map((u) => (
                    <tr key={u.actor} className="border-b border-slate-50 last:border-0">
                      <td className="py-2">
                        <div className="font-head text-[#0B2545]">{u.name}</div>
                        <div className="text-xs text-slate-400">{u.actor}</div>
                      </td>
                      <td className="py-2 text-right font-head font-bold text-[#00857C]">{u.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
          <Panel title="Most visited screens">
            {summary.top_screens.length === 0 ? <Empty>No screen views yet</Empty> : (
              <table className="w-full text-sm" data-testid="activity-top-screens">
                <tbody>
                  {summary.top_screens.map((s) => (
                    <tr key={s.screen} className="border-b border-slate-50 last:border-0">
                      <td className="py-2 text-[#0B2545]">{s.screen}</td>
                      <td className="py-2 text-right font-head font-bold text-[#0369a1]">{s.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </div>
      )}

      <Panel title="Activity timeline" className="overflow-hidden">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <div className="overline text-slate-500 mb-1">User</div>
            <Select testid="activity-filter-user" value={actor} onChange={setActor} placeholder="All users"
              options={(data?.users || []).map((u) => ({ value: u.actor, label: `${u.name} (${u.actor})` }))} />
          </div>
          <div>
            <div className="overline text-slate-500 mb-1">Type</div>
            <Select testid="activity-filter-type" value={type} onChange={setType} placeholder="All types" options={TYPE_OPTIONS} />
          </div>
          <div>
            <div className="overline text-slate-500 mb-1">From</div>
            <input data-testid="activity-filter-start" type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="border border-slate-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00857C] bg-white" />
          </div>
          <div>
            <div className="overline text-slate-500 mb-1">To</div>
            <input data-testid="activity-filter-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              className="border border-slate-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00857C] bg-white" />
          </div>
          <Button data-testid="activity-clear-filters" onClick={clearFilters} variant="outline" className="rounded-sm border-slate-300 font-head">Clear</Button>
        </div>

        {events.length === 0 ? <Empty>No activity for the selected filters</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="activity-table">
              <thead>
                <tr className="text-left overline text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-4">Time</th>
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Role</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Screen / Action</th>
                  <th className="py-2">Detail</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id} className="border-b border-slate-50 last:border-0 align-top" data-testid="activity-row">
                    <td className="py-2 pr-4 whitespace-nowrap text-slate-500">{fmt(e.at)}</td>
                    <td className="py-2 pr-4">
                      <div className="font-head text-[#0B2545]">{e.name || e.actor}</div>
                      <div className="text-xs text-slate-400">{e.actor}</div>
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap text-xs text-slate-500">{(e.role || "").replace(/_/g, " ")}</td>
                    <td className="py-2 pr-4"><TypeBadge type={e.type} /></td>
                    <td className="py-2 pr-4 font-head text-[#0B2545]">{e.type === "screen_view" ? e.screen : e.action}</td>
                    <td className="py-2 text-slate-500">{e.type === "screen_view" ? "" : e.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
